/**
 * Enhanced Response Viewer Component for dev-tools
 * Uses both structured view and advanced JSON tree viewer
 */
import React, { useState } from "react";

import {
  AccountCircle as AccountCircleIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Code as CodeIcon,
  DataObject as DataObjectIcon,
  Error as ErrorIcon,
  Event as EventIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  List as ListIcon,
  AccountTree as TreeViewIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import { formatEnumForDisplay } from "../utils/enum-utils";
import { JsonTreeViewer } from "./json-tree-viewer";

interface ResponseViewerProps {
  response: unknown;
  request?: unknown;
  methodName?: string;
  enableEdit?: boolean;
  onEdit?: (editInfo: unknown) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`response-tabpanel-${index}`}
      aria-labelledby={`response-tab-${index}`}
      style={{ textAlign: "left" }}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 1, textAlign: "left" }}>{children}</Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `response-tab-${index}`,
    "aria-controls": `response-tabpanel-${index}`,
  };
}

export const EnhancedResponseViewer: React.FC<ResponseViewerProps> = ({
  response,
  enableEdit = false,
  onEdit,
}) => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Helper function to get response size
  const getResponseSize = (data: unknown): string => {
    const jsonString = JSON.stringify(data);
    const bytes = new Blob([jsonString]).size;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Helper function to determine response type
  const getResponseType = (data: unknown): string => {
    if (data === null || data === undefined) return "empty";
    if (typeof data === "string") return "string";
    if (typeof data === "number") return "number";
    if (typeof data === "boolean") return "boolean";
    if (Array.isArray(data)) return "array";
    if (typeof data === "object") {
      const obj = data as Record<string, unknown>;

      // Check for common response patterns
      if (obj.success !== undefined) return "status";
      if (obj.error || obj.message) return "error";
      if (obj.users || obj.user) return "user";
      if (obj.organization || obj.org) return "organization";
      if (obj.results || obj.data) return "list";
      if (obj.id || obj.userId || obj.orgId) return "entity";

      return "object";
    }
    return "unknown";
  };

  // Helper function to format timestamps
  const formatTimestamp = (value: unknown): string => {
    if (typeof value === "string") {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleString();
      }
    }
    return String(value);
  };

  // Helper function to get icon for data type
  const getTypeIcon = (type: string) => {
    switch (type) {
      case "user":
        return <AccountCircleIcon />;
      case "organization":
        return <BusinessIcon />;
      case "list":
        return <ListIcon />;
      case "event":
        return <EventIcon />;
      case "object":
        return <DataObjectIcon />;
      case "error":
        return <ErrorIcon color="error" />;
      case "status":
        return <CheckCircleIcon color="success" />;
      default:
        return <InfoIcon />;
    }
  };

  // Render individual field value
  const renderFieldValue = (
    key: string,
    value: unknown,
    depth = 0
  ): React.ReactNode => {
    if (value === null || value === undefined) {
      return renderNullValue();
    }

    if (typeof value === "boolean") {
      return renderBooleanValue(value);
    }

    if (typeof value === "string") {
      return renderStringValue(key, value);
    }

    if (typeof value === "number") {
      return renderNumberValue(key, value);
    }

    if (Array.isArray(value)) {
      return renderArrayValue(value, depth);
    }

    if (typeof value === "object" && depth < 3) {
      return renderObjectValue(value, depth);
    }

    return renderFallbackValue(value);
  };

  /**
   * Render null/undefined values
   */
  const renderNullValue = (): React.ReactNode => {
    return <Chip label="null" size="small" variant="outlined" />;
  };

  /**
   * Render boolean values with colored chips
   */
  const renderBooleanValue = (value: boolean): React.ReactNode => {
    return (
      <Chip
        label={String(value)}
        size="small"
        color={value ? "success" : "default"}
        variant="outlined"
        data-testid={`boolean-value-${value}`}
      />
    );
  };

  /**
   * Render string values with special formatting for timestamps and IDs
   */
  const renderStringValue = (key: string, value: string): React.ReactNode => {
    const lowerKey = key.toLowerCase();

    // Check if it's a timestamp
    if (lowerKey.includes("time") || lowerKey.includes("date")) {
      return (
        <Box>
          <Typography variant="body2" color="text.primary">
            {formatTimestamp(value)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {value}
          </Typography>
        </Box>
      );
    }

    // Check if it's an ID
    if (lowerKey.includes("id")) {
      return (
        <Typography
          variant="body2"
          sx={{
            fontFamily: "monospace",
            bgcolor: "grey.100",
            p: 0.5,
            borderRadius: 1,
            display: "inline-block",
          }}
        >
          {value}
        </Typography>
      );
    }

    // Regular string
    return <Typography variant="body2">{value}</Typography>;
  };

  /**
   * Render number values with enum detection
   */
  const renderNumberValue = (key: string, value: number): React.ReactNode => {
    // Try to detect if this might be an enum value
    const enumInfo = formatEnumForDisplay(key, value);

    if (enumInfo.stringValue) {
      // This appears to be an enum - show both string and numeric value
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {enumInfo.displayText}
          </Typography>
          <Chip
            label={`${enumInfo.stringValue} (${enumInfo.numeric})`}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ fontSize: "0.75rem" }}
          />
        </Box>
      );
    }

    // Regular number display
    return (
      <Typography variant="body2" color="primary">
        {value.toLocaleString()}
      </Typography>
    );
  };

  /**
   * Render array values with item preview
   */
  const renderArrayValue = (
    value: unknown[],
    depth: number
  ): React.ReactNode => {
    if (value.length === 0) {
      return <Chip label="empty array" size="small" variant="outlined" />;
    }

    return (
      <Box>
        <Typography variant="caption" color="text.secondary">
          Array ({value.length} items)
        </Typography>
        <List dense sx={{ maxHeight: 200, overflow: "auto" }}>
          {value.slice(0, 10).map((item, index) => (
            <ListItem key={index} divider>
              <ListItemText
                primary={renderFieldValue(`item_${index}`, item, depth + 1)}
              />
            </ListItem>
          ))}
          {value.length > 10 && (
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="caption" color="text.secondary">
                    ... and {value.length - 10} more items
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>
      </Box>
    );
  };

  /**
   * Render object values with nested structure
   */
  const renderObjectValue = (
    value: unknown,
    depth: number
  ): React.ReactNode => {
    const obj = value as Record<string, unknown>;
    const entries = Object.entries(obj);

    if (entries.length === 0) {
      return <Chip label="empty object" size="small" variant="outlined" />;
    }

    return (
      <Card variant="outlined" sx={{ mt: 1, maxWidth: 600 }}>
        <CardContent sx={{ p: 1 }}>
          {entries.slice(0, 5).map(([nestedKey, nestedValue]) => (
            <Box key={nestedKey} sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {nestedKey}:
              </Typography>
              <Box sx={{ ml: 1 }}>
                {renderFieldValue(nestedKey, nestedValue, depth + 1)}
              </Box>
            </Box>
          ))}
          {entries.length > 5 && (
            <Typography variant="caption" color="text.secondary">
              ... and {entries.length - 5} more fields
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  /**
   * Render fallback for other value types
   */
  const renderFallbackValue = (value: unknown): React.ReactNode => {
    return (
      <Typography variant="body2" color="text.secondary">
        {String(value)}
      </Typography>
    );
  };

  // Render structured view
  const renderStructuredView = (): React.ReactNode => {
    if (!response) {
      return renderNoDataAlert();
    }

    const responseType = getResponseType(response);

    // Handle different response types
    if (responseType === "error") return renderErrorResponse(response);
    if (responseType === "status") return renderStatusResponse(response);
    if (["string", "number", "boolean"].includes(responseType))
      return renderPrimitiveResponse(response);
    if (responseType === "array") return renderArrayResponse(response);
    if (typeof response === "object") return renderObjectResponse(response);

    return renderUnknownResponse();
  };

  /**
   * Render alert for no data
   */
  const renderNoDataAlert = (): React.ReactNode => {
    return (
      <Alert severity="info" sx={{ mb: 2 }} data-testid="no-data-alert">
        No response data to display
      </Alert>
    );
  };

  /**
   * Render error response with details
   */
  const renderErrorResponse = (errorResponse: unknown): React.ReactNode => {
    const errorObj = errorResponse as Record<string, unknown>;

    return (
      <Alert
        data-testid="error-response-alert"
        severity="error"
        sx={{
          mb: 1.5,
          py: 0.5,
          "& .MuiAlert-message": {
            width: "100%",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
              {String(
                errorObj.error || errorObj.message || "An error occurred"
              )}
            </Typography>
            {errorObj.code ? (
              <Typography variant="caption" color="text.secondary">
                Error Code: {String(errorObj.code)}
              </Typography>
            ) : null}
          </Box>
          <Chip
            label="Error"
            size="small"
            color="error"
            variant="outlined"
            sx={{ ml: 1 }}
            data-testid="error-chip"
          />
        </Box>
      </Alert>
    );
  };

  /**
   * Render status response with success/failure indication
   */
  const renderStatusResponse = (statusResponse: unknown): React.ReactNode => {
    const statusObj = statusResponse as Record<string, unknown>;
    const isSuccess = Boolean(statusObj.success);

    return (
      <Alert
        data-testid="status-response-alert"
        severity={isSuccess ? "success" : "error"}
        sx={{
          mb: 1.5,
          py: 0.5,
          "& .MuiAlert-message": {
            width: "100%",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, mb: 0.25 }}
              data-testid={`status-text-${isSuccess ? "success" : "failed"}`}
            >
              {isSuccess ? "Success" : "Failed"}
            </Typography>
            {statusObj.message ? (
              <Typography variant="body2">
                {String(statusObj.message)}
              </Typography>
            ) : null}
          </Box>
          <Chip
            label={isSuccess ? "Success" : "Failed"}
            size="small"
            color={isSuccess ? "success" : "error"}
            variant="outlined"
            sx={{ ml: 1 }}
            data-testid={`status-chip-${isSuccess ? "success" : "failed"}`}
          />
        </Box>
      </Alert>
    );
  };

  /**
   * Render primitive response (string, number, boolean)
   */
  const renderPrimitiveResponse = (
    primitiveResponse: unknown
  ): React.ReactNode => {
    return (
      <Card data-testid="primitive-response-card">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Response Value
          </Typography>
          {renderFieldValue("response", primitiveResponse)}
        </CardContent>
      </Card>
    );
  };

  /**
   * Render array response with item count
   */
  const renderArrayResponse = (arrayResponse: unknown): React.ReactNode => {
    const array = arrayResponse as unknown[];

    return (
      <Card data-testid="array-response-card">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Response Array ({array.length} items)
          </Typography>
          {renderFieldValue("items", arrayResponse)}
        </CardContent>
      </Card>
    );
  };

  /**
   * Render object response with structured view
   */
  const renderObjectResponse = (objectResponse: unknown): React.ReactNode => {
    const obj = objectResponse as Record<string, unknown>;
    const entries = Object.entries(obj);
    const responseType = getResponseType(objectResponse);

    return (
      <Box>
        {renderResponseHeader(responseType, entries, objectResponse)}
        {renderCollapsibleFields(entries)}
      </Box>
    );
  };

  /**
   * Render response header with summary information
   */
  const renderResponseHeader = (
    responseType: string,
    entries: Array<[string, unknown]>,
    responseData: unknown
  ): React.ReactNode => {
    return (
      <Paper
        variant="outlined"
        data-testid="response-header"
        sx={{
          p: 1.5,
          mb: 1.5,
          bgcolor: "action.hover",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ListItemIcon sx={{ minWidth: 28 }}>
              {getTypeIcon(responseType)}
            </ListItemIcon>
            <Typography variant="subtitle1" sx={{ mb: 0, fontWeight: 600 }}>
              Response Data
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Chip
              data-testid="field-count-chip"
              label={`${entries.length} field${
                entries.length !== 1 ? "s" : ""
              }`}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip
              data-testid="response-type-chip"
              label={responseType}
              size="small"
              color="info"
              variant="outlined"
            />
            <Chip
              data-testid="response-size-chip"
              label={getResponseSize(responseData)}
              size="small"
              color="default"
              variant="outlined"
            />
          </Box>
        </Box>
      </Paper>
    );
  };

  /**
   * Render collapsible fields for object entries
   */
  const renderCollapsibleFields = (
    entries: Array<[string, unknown]>
  ): React.ReactNode => {
    return (
      <Stack spacing={0.5} data-testid="collapsible-fields">
        {entries.map(([key, value]) => (
          <Accordion
            key={key}
            data-testid={`field-accordion-${key}`}
            defaultExpanded={entries.length <= 3}
            sx={{
              "&:before": {
                display: "none",
              },
              boxShadow: "none",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 44,
                "&.Mui-expanded": {
                  minHeight: 44,
                },
                "& .MuiAccordionSummary-content": {
                  "&.Mui-expanded": {
                    margin: "12px 0",
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: "100%",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600 }}
                  data-testid={`field-name-${key}`}
                >
                  {key}
                </Typography>
                <Chip
                  data-testid={`field-type-${key}`}
                  label={
                    Array.isArray(value)
                      ? `array[${value.length}]`
                      : typeof value
                  }
                  size="small"
                  variant="outlined"
                  sx={{ ml: "auto" }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1 }}>
              <div data-testid={`field-value-${key}`}>
                {renderFieldValue(key, value)}
              </div>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    );
  };

  /**
   * Render fallback for unknown response types
   */
  const renderUnknownResponse = (): React.ReactNode => {
    return (
      <Alert severity="warning">
        <Typography>Unable to parse response data</Typography>
      </Alert>
    );
  };

  // Render raw JSON view
  const renderRawJsonView = (): React.ReactNode => {
    return (
      <Paper
        variant="outlined"
        data-testid="raw-json-view"
        sx={{
          p: 3,
          bgcolor: "background.default",
          textAlign: "left",
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <pre
          style={{
            margin: 0,
            fontFamily: "Monaco, Consolas, 'Courier New', monospace",
            fontSize: "0.875rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxHeight: "500px",
            overflow: "auto",
            textAlign: "left",
            lineHeight: 1.6,
            color: "inherit",
          }}
        >
          {JSON.stringify(response, null, 2)}
        </pre>
      </Paper>
    );
  };

  return (
    <Box sx={{ width: "100%", textAlign: "left" }}>
      {/* Overview Information - Always Visible */}
      <Box sx={{ mb: 1.5 }}>{renderStructuredView()}</Box>

      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="response view tabs"
        >
          <Tab
            icon={<TreeViewIcon />}
            iconPosition="start"
            label="JSON Tree"
            {...a11yProps(0)}
          />
          <Tab
            icon={<CodeIcon />}
            iconPosition="start"
            label="Raw JSON"
            {...a11yProps(1)}
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <CustomTabPanel value={tabValue} index={0}>
        {response ? (
          <JsonTreeViewer
            data={response}
            title=""
            maxHeight={600}
            defaultCollapsed={false}
            enableCopy={true}
            enableEdit={enableEdit}
            onEdit={onEdit}
          />
        ) : (
          <Alert severity="info" data-testid="no-response-data-alert">
            No response data to display
          </Alert>
        )}
      </CustomTabPanel>

      <CustomTabPanel value={tabValue} index={1}>
        {renderRawJsonView()}
      </CustomTabPanel>
    </Box>
  );
};
